import React, { useState } from "react";
import {
  MyRegistrationItem,
  MyRegistrationEnrollee,
} from "../../api/getMyRegistrations/types";
import axiosInstance from "@/api/axiosInstance";
import { toast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarDays,
  Download,
  FileText,
  Loader2,
  MapPin,
  Video,
} from "lucide-react";
import { Link } from "react-router-dom";
import { groupSessionDays } from "@/modules/eventRegistration/shared/utils/groupSessionDays";

interface MyRegistrationCardProps {
  registration: MyRegistrationItem;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
  /**
   * Past registrations get a slightly dimmed presentation so they
   * recede visually next to current/future ones.
   */
  variant?: "active" | "past";
}

function formatTimeRange(
  startDate: string | null,
  endDate: string | null,
  timezoneShortName: string | null,
  timezoneIdentifier: string | null,
): string {
  if (!startDate) return "Date TBD";

  const tz = timezoneIdentifier ?? undefined;
  const start = new Date(startDate);
  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });

  let result = startTime;

  if (endDate) {
    const end = new Date(endDate);
    const endTime = end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    result += ` – ${endTime}`;
  }

  if (timezoneShortName) {
    result += ` ${timezoneShortName}`;
  }

  return result;
}

function formatVenue(
  venueName: string | null,
  venueCity: string | null,
  venueState: string | null,
): string | null {
  if (!venueName && !venueCity) return null;
  const parts: string[] = [];
  if (venueName) parts.push(venueName);
  const location = [venueCity, venueState].filter(Boolean).join(", ");
  if (location) parts.push(location);
  return parts.join(" · ");
}

function DateTile({
  startDate,
  timezoneIdentifier,
  dim,
}: {
  startDate: string | null;
  timezoneIdentifier: string | null;
  dim: boolean;
}) {
  if (!startDate) {
    return (
      <div
        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex flex-col items-center justify-center bg-gray-100 dark:bg-slate-700/50 ${
          dim ? "opacity-60" : ""
        }`}
      >
        <CalendarDays className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        <span className="mt-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
          TBD
        </span>
      </div>
    );
  }

  const tz = timezoneIdentifier ?? undefined;
  const d = new Date(startDate);
  const month = d
    .toLocaleDateString("en-US", { month: "short", timeZone: tz })
    .toUpperCase();
  const day = d.toLocaleDateString("en-US", { day: "numeric", timeZone: tz });
  const weekday = d
    .toLocaleDateString("en-US", { weekday: "short", timeZone: tz })
    .toUpperCase();

  return (
    <div
      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700 flex-shrink-0 ${
        dim ? "opacity-60" : ""
      }`}
      data-testid="registration-date-tile"
    >
      <span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">
        {month}
      </span>
      <span className="text-2xl sm:text-3xl font-light leading-none text-gray-900 dark:text-white">
        {day}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-0.5">
        {weekday}
      </span>
    </div>
  );
}

function statusBadge(enrollee: MyRegistrationEnrollee) {
  if (enrollee.isRefunded) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Refunded
      </span>
    );
  }
  if (enrollee.isCancelled) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Cancelled
      </span>
    );
  }
  if (enrollee.isWaitlisted) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Waitlisted
        {enrollee.waitlistPosition ? ` · #${enrollee.waitlistPosition}` : ""}
      </span>
    );
  }
  return null;
}

function isWithinCancellationWindow(startDate: string | null): boolean {
  if (!startDate) return false;
  const eventStart = new Date(startDate);
  const now = new Date();
  const fourteenDaysFromNow = new Date(
    now.getTime() + 14 * 24 * 60 * 60 * 1000,
  );
  return eventStart >= fourteenDaysFromNow;
}

function EnrolleeRow({
  enrollee,
  canManageRegistrations,
  onCancelSuccess,
  startDate,
  allowCancel,
}: {
  enrollee: MyRegistrationEnrollee;
  canManageRegistrations: boolean;
  onCancelSuccess: () => void;
  startDate: string | null;
  allowCancel: boolean;
}) {
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const withinWindow = isWithinCancellationWindow(startDate);
  const canCancel =
    allowCancel &&
    canManageRegistrations &&
    !enrollee.isCancelled &&
    !enrollee.isRefunded;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axiosInstance.post(
        `/api/private/enrollments/${enrollee.enrollmentId}/cancel`,
        { reason },
      );
      toast({
        title: "Registration cancelled",
        description: `${enrollee.firstName} ${enrollee.lastName}'s registration has been cancelled.`,
      });
      setShowCancelForm(false);
      setReason("");
      onCancelSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description:
          err?.response?.data?.message || "Failed to cancel registration.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-900 dark:text-white truncate">
            {enrollee.firstName} {enrollee.lastName}
          </p>
          {enrollee.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {enrollee.email}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {statusBadge(enrollee)}
        </div>
      </div>

      {canCancel && !showCancelForm && (
        <button
          className="mt-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          onClick={() => setShowCancelForm(true)}
          type="button"
        >
          Cancel registration
        </button>
      )}

      {showCancelForm && (
        <div className="mt-2">
          {!withinWindow ? (
            <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 mb-2">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Cancellations within 14 days of the event start date must be
                handled by the training team.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Please contact{" "}
                <span className="font-medium">training@mycertainpath.com</span>{" "}
                or call <span className="font-medium">945-270-8056</span>.
              </p>
              <div className="mt-2 flex justify-end">
                <Button
                  className="h-7 text-xs"
                  onClick={() => setShowCancelForm(false)}
                  size="sm"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Textarea
                className="mb-2"
                maxLength={255}
                onChange={(e) => setReason(e.target.value.slice(0, 255))}
                placeholder="Reason for cancellation..."
                rows={2}
                value={reason}
              />
              <div className="flex justify-end gap-2">
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling}
                  onClick={() => {
                    setShowCancelForm(false);
                    setReason("");
                  }}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling || !reason.trim()}
                  onClick={handleCancel}
                  size="sm"
                >
                  {cancelling && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Confirm
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const MyRegistrationCard: React.FC<MyRegistrationCardProps> = ({
  registration,
  canManageRegistrations,
  onCancelSuccess,
  variant = "active",
}) => {
  const {
    eventUuid,
    eventName,
    sessionName,
    startDate,
    endDate,
    sessionDays,
    timezoneShortName,
    timezoneIdentifier,
    venueName,
    venueCity,
    venueState,
    confirmationNumber,
    isSessionCancelled,
    virtualLink,
    enrollments,
    attachments,
  } = registration;

  // The virtual join link and session materials live on the session, but they
  // should disappear for anyone whose seat is gone. Show them only while at
  // least one enrollment on this card is still active (not cancelled/refunded).
  const hasActiveEnrollment = enrollments.some(
    (e) => !e.isCancelled && !e.isRefunded,
  );

  const time = formatTimeRange(
    startDate,
    endDate,
    timezoneShortName,
    timezoneIdentifier,
  );
  // Broken-out session days render as stacked "date · time" lines; legacy
  // sessions fall back to the single time-only line below.
  const sessionDayGroups = groupSessionDays(
    sessionDays,
    timezoneIdentifier,
    timezoneShortName,
  );
  const venue = formatVenue(venueName, venueCity, venueState);
  const isPast = variant === "past";

  return (
    <div
      className={`bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 ${
        isPast ? "opacity-90" : ""
      }`}
      data-testid="my-registration-card"
    >
      <div className="flex gap-4">
        <DateTile
          dim={isPast}
          startDate={startDate}
          timezoneIdentifier={timezoneIdentifier}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3
                className={`text-base font-normal truncate ${
                  isPast
                    ? "text-gray-600 dark:text-gray-300"
                    : "text-gray-900 dark:text-white"
                }`}
              >
                {eventName}
              </h3>
              {sessionName && sessionName !== eventName && (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {sessionName}
                </p>
              )}
            </div>
            {isSessionCancelled && (
              <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                Session Cancelled
              </span>
            )}
          </div>

          <div className="mt-1.5 space-y-0.5">
            {sessionDayGroups.length > 0 ? (
              sessionDayGroups.map((group, i) => (
                <p
                  className="text-sm text-gray-500 dark:text-gray-400"
                  key={`${group.dateLabel}-${i}`}
                >
                  {group.dateLabel} · {group.timeLabel}
                </p>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">{time}</p>
            )}
            {venue && (
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{venue}</span>
              </p>
            )}
            {virtualLink && hasActiveEnrollment && (
              <p className="text-sm flex items-center gap-1.5">
                <Video className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <a
                  className="text-blue-600 dark:text-blue-400 underline truncate hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  href={virtualLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  Join link
                </a>
              </p>
            )}
            {hasActiveEnrollment && attachments && attachments.length > 0 && (
              <div className="pt-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                  Materials
                </p>
                <ul className="mt-1 space-y-0.5">
                  {attachments.map((file) => (
                    <li key={file.fileUuid}>
                      <a
                        className="text-sm text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors inline-flex items-center gap-1.5"
                        href={file.downloadUrl ?? "#"}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <Download className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">
                          {file.name ?? "Download"}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {confirmationNumber && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Confirmation: {confirmationNumber}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50 divide-y divide-gray-100 dark:divide-slate-700/50">
        {enrollments.map((enrollee) => (
          <EnrolleeRow
            allowCancel={!isPast}
            canManageRegistrations={canManageRegistrations}
            enrollee={enrollee}
            key={enrollee.enrollmentId}
            onCancelSuccess={onCancelSuccess}
            startDate={startDate}
          />
        ))}
      </div>

      {eventUuid && (
        <div className="mt-3 flex justify-end">
          <Button asChild size="sm" variant="ghost">
            <Link to={`/event-registration/events/${eventUuid}`}>
              View event details
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default MyRegistrationCard;
